import { ExternalLink, EyeOff, Plus } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { mensajeDe } from '@/api/errors';
import { PermissionGate } from '@/auth/PermissionGate';
import { useCan } from '@/auth/queries';
import { Campo } from '@/components/Campo';
import { ConfirmarAccion } from '@/components/ConfirmarAccion';
import { Cargando, Encabezado, ErrorCarga, Vacio } from '@/components/Estados';
import { Modal } from '@/components/Modal';
import { fechaLarga } from '@/lib/formato';

import {
  ETAPAS_TIPICAS,
  useBorrarEtapa,
  useEtapas,
  useFechasClave,
  useGuardarEtapa,
  useGuardarFechaClave,
  type EtapaEnarm,
  type FechaClave,
} from './queries';

/** Fila editable de una fecha clave: nombre y fecha; se guarda solo si cambió. */
function FilaFechaClave({ f, editable }: { f: FechaClave; editable: boolean }) {
  const [nombre, setNombre] = useState(f.name);
  const [fecha, setFecha] = useState(f.date ?? '');
  const guardar = useGuardarFechaClave();
  const cambio = nombre.trim() !== f.name || fecha !== (f.date ?? '');
  return (
    <tr>
      <td className="tabular text-texto-3">{f.id}</td>
      <td>
        {editable ? (
          <input
            className="input-vidrio py-1.5"
            value={nombre}
            maxLength={40}
            aria-label={`Nombre de la fecha ${String(f.id)}`}
            onChange={(e) => setNombre(e.target.value)}
          />
        ) : (
          <span className="font-bold">{f.name}</span>
        )}
      </td>
      <td>
        {editable ? (
          <input
            type="date"
            className="input-vidrio py-1.5"
            value={fecha}
            aria-label={`Fecha de ${f.name}`}
            onChange={(e) => setFecha(e.target.value)}
          />
        ) : (
          fechaLarga(f.date)
        )}
      </td>
      <td className="max-w-xs text-xs text-texto-2">{f.usage ?? '—'}</td>
      <td className="text-right">
        {editable && (
          <button
            type="button"
            className="btn-cs btn-primario px-3 py-1 text-xs"
            disabled={!cambio || nombre.trim() === '' || guardar.isPending}
            onClick={() =>
              guardar.mutate(
                { id: f.id, name: nombre.trim(), date: fecha === '' ? null : fecha },
                {
                  onSuccess: () => toast.success(`«${nombre.trim()}» guardada.`),
                  onError: (e) => toast.error(mensajeDe(e)),
                },
              )
            }
          >
            Guardar
          </button>
        )}
      </td>
    </tr>
  );
}

function FechasClave() {
  const q = useFechasClave();
  const editable = useCan('configuracion', 'editar');
  return (
    <section className="space-y-3">
      <div>
        <h2 className="text-lg font-extrabold">Fechas clave (cat__enarm)</h2>
        <p className="text-sm text-texto-2">
          La plataforma las lee por número: cambiar la fecha del ENARM mueve el límite de las rondas
          del plan de estudio y la cuenta regresiva. No se agregan ni se borran filas.
        </p>
      </div>
      {q.isPending && <Cargando />}
      {q.isError && <ErrorCarga error={q.error} onReintentar={() => void q.refetch()} />}
      {q.data?.length === 0 && <Vacio titulo="Sin fechas clave" />}
      {q.data && q.data.length > 0 && (
        <div className="panel-vidrio overflow-x-auto p-0">
          <table className="tabla-cs w-full text-sm">
            <thead>
              <tr>
                <th>#</th>
                <th>Nombre</th>
                <th>Fecha</th>
                <th>Dónde se usa</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {q.data.map((f) => (
                <FilaFechaClave
                  key={`${String(f.id)}-${f.name}-${f.date ?? ''}`}
                  f={f}
                  editable={editable}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

interface FormEtapaDatos {
  title: string;
  description: string;
  date: string;
  endDate: string;
  timeNote: string;
  sourceUrl: string;
  isVisible: boolean;
}

function FormEtapa({ actual, onListo }: { actual: EtapaEnarm | null; onListo: () => void }) {
  const [f, setF] = useState<FormEtapaDatos>({
    title: actual?.title ?? '',
    description: actual?.description ?? '',
    date: actual?.date ?? '',
    endDate: actual?.endDate ?? '',
    timeNote: actual?.timeNote ?? '',
    sourceUrl: actual?.sourceUrl ?? '',
    isVisible: actual?.isVisible ?? true,
  });
  const poner = (c: Partial<FormEtapaDatos>) => setF((v) => ({ ...v, ...c }));
  const guardar = useGuardarEtapa();
  const nulo = (v: string) => (v.trim() === '' ? null : v.trim());
  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (f.endDate !== '' && f.endDate < f.date) {
          toast.error('La fecha de fin no puede ser anterior a la de inicio.');
          return;
        }
        guardar.mutate(
          {
            id: actual?.id,
            body: {
              title: f.title.trim(),
              description: nulo(f.description),
              date: f.date,
              endDate: nulo(f.endDate),
              timeNote: nulo(f.timeNote),
              sourceUrl: nulo(f.sourceUrl),
              isVisible: f.isVisible,
            },
          },
          {
            onSuccess: () => {
              toast.success(actual ? 'Etapa guardada.' : 'Etapa agregada al calendario.');
              onListo();
            },
            onError: (err) => toast.error(mensajeDe(err)),
          },
        );
      }}
    >
      <Campo etiqueta="Etapa" ayuda="Elige una etapa típica o escribe otra.">
        <input
          className="input-vidrio"
          list="etapas-tipicas"
          value={f.title}
          required
          minLength={2}
          maxLength={120}
          onChange={(e) => poner({ title: e.target.value })}
        />
        <datalist id="etapas-tipicas">
          {ETAPAS_TIPICAS.map((t) => (
            <option key={t} value={t} />
          ))}
        </datalist>
      </Campo>
      <div className="grid gap-3 sm:grid-cols-3">
        <Campo etiqueta="Desde">
          <input
            type="date"
            className="input-vidrio"
            value={f.date}
            required
            onChange={(e) => poner({ date: e.target.value })}
          />
        </Campo>
        <Campo etiqueta="Hasta" ayuda="Vacío si es de un solo día.">
          <input
            type="date"
            className="input-vidrio"
            value={f.endDate}
            min={f.date}
            onChange={(e) => poner({ endDate: e.target.value })}
          />
        </Campo>
        <Campo etiqueta="Horario" ayuda="Ej. «10:00 a 16:00 h».">
          <input
            className="input-vidrio"
            value={f.timeNote}
            maxLength={80}
            onChange={(e) => poner({ timeNote: e.target.value })}
          />
        </Campo>
      </div>
      <Campo etiqueta="Descripción (opcional)">
        <textarea
          className="input-vidrio min-h-24"
          value={f.description}
          maxLength={1000}
          onChange={(e) => poner({ description: e.target.value })}
        />
      </Campo>
      <Campo etiqueta="Fuente oficial (opcional)" ayuda="URL de la convocatoria o del aviso.">
        <input
          type="url"
          className="input-vidrio"
          value={f.sourceUrl}
          maxLength={255}
          placeholder="https://…"
          onChange={(e) => poner({ sourceUrl: e.target.value })}
        />
      </Campo>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={f.isVisible}
          onChange={(e) => poner({ isVisible: e.target.checked })}
        />
        Visible para los alumnos
      </label>
      <div className="flex justify-end gap-2">
        <button type="button" className="btn-cs btn-contorno" onClick={onListo}>
          Cancelar
        </button>
        <button type="submit" className="btn-cs btn-primario" disabled={guardar.isPending}>
          Guardar
        </button>
      </div>
    </form>
  );
}

function Etapas() {
  const q = useEtapas();
  const editable = useCan('configuracion', 'editar');
  const borrar = useBorrarEtapa();
  const [editando, setEditando] = useState<EtapaEnarm | 'nueva' | null>(null);
  const [borrando, setBorrando] = useState<EtapaEnarm | null>(null);
  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-extrabold">Calendario del ENARM para alumnos</h2>
          <p className="text-sm text-texto-2">
            Las etapas visibles aparecen en “Mi plan de estudio” de todos los alumnos con Temario,
            junto a sus temas y sus eventos personales.
          </p>
        </div>
        {editable && (
          <button
            type="button"
            className="btn-cs btn-primario"
            onClick={() => setEditando('nueva')}
          >
            <Plus size={16} aria-hidden /> Nueva etapa
          </button>
        )}
      </div>
      {q.isPending && <Cargando />}
      {q.isError && <ErrorCarga error={q.error} onReintentar={() => void q.refetch()} />}
      {q.data?.length === 0 && (
        <Vacio titulo="Sin etapas">
          Agrega las fechas de la convocatoria vigente (preregistro, pago, sedes, aplicación…).
        </Vacio>
      )}
      {q.data && q.data.length > 0 && (
        <div className="panel-vidrio overflow-x-auto p-0">
          <table className="tabla-cs w-full text-sm">
            <thead>
              <tr>
                <th>Etapa</th>
                <th>Fechas</th>
                <th>Horario</th>
                <th>Fuente</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {q.data.map((e) => (
                <tr key={e.id} className={e.isVisible ? '' : 'opacity-60'}>
                  <td>
                    <span className="font-bold">{e.title}</span>
                    {!e.isVisible && (
                      <span className="insignia ml-2 inline-flex items-center gap-1">
                        <EyeOff size={12} aria-hidden /> Oculta
                      </span>
                    )}
                    {e.description && (
                      <p className="mt-0.5 line-clamp-2 max-w-md text-xs text-texto-3">
                        {e.description}
                      </p>
                    )}
                  </td>
                  <td className="whitespace-nowrap">
                    {fechaLarga(e.date)}
                    {e.endDate && ` – ${fechaLarga(e.endDate)}`}
                  </td>
                  <td className="text-xs">{e.timeNote ?? '—'}</td>
                  <td>
                    {e.sourceUrl ? (
                      <a
                        href={e.sourceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-azul-texto hover:underline"
                      >
                        <ExternalLink size={12} aria-hidden /> Abrir
                      </a>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="space-x-2 text-right whitespace-nowrap">
                    {editable && (
                      <>
                        <button
                          type="button"
                          className="btn-cs btn-contorno px-2 py-1 text-xs"
                          onClick={() => setEditando(e)}
                        >
                          Editar
                        </button>
                        <button
                          type="button"
                          className="btn-cs btn-peligro px-2 py-1 text-xs"
                          onClick={() => setBorrando(e)}
                        >
                          Eliminar
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Modal
        abierto={editando !== null}
        onCerrar={() => setEditando(null)}
        titulo={editando === 'nueva' ? 'Nueva etapa' : `Editar ${editando?.title ?? ''}`}
        ancho="max-w-2xl"
      >
        {editando !== null && (
          <FormEtapa
            key={editando === 'nueva' ? 'nueva' : editando.id}
            actual={editando === 'nueva' ? null : editando}
            onListo={() => setEditando(null)}
          />
        )}
      </Modal>
      <ConfirmarAccion
        abierto={borrando !== null}
        onCerrar={() => setBorrando(null)}
        titulo={`Eliminar ${borrando?.title ?? ''}`}
        descripcion="Desaparece del calendario de todos los alumnos. Si solo quieres esconderla un tiempo, edítala y quita “Visible para los alumnos”."
        textoBoton="Eliminar"
        peligro
        ocupado={borrar.isPending}
        onConfirmar={() =>
          borrando &&
          borrar.mutate(borrando.id, {
            onSuccess: () => {
              setBorrando(null);
              toast.success('Etapa eliminada.');
            },
            onError: (err) => toast.error(mensajeDe(err)),
          })
        }
      />
    </section>
  );
}

/** Configuración → Fechas ENARM: fechas clave de `cat__enarm` y etapas del calendario de alumnos. */
export function FechasEnarmPage() {
  return (
    <PermissionGate seccion="configuracion">
      <Encabezado
        titulo="Fechas ENARM"
        descripcion="Fechas del ciclo que usa la plataforma y calendario oficial que ven los alumnos."
      />
      <div className="space-y-10">
        <Etapas />
        <FechasClave />
      </div>
    </PermissionGate>
  );
}
